import { useSearchParams } from "react-router-dom";

import { CalendarRange, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Segmented } from "@/components/shared/segmented";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DASHBOARD_PRESETS,
  type DashboardParams,
  type DashboardPreset,
  matchPreset,
  presetRange,
} from "@/lib/dashboard";
import { withParams } from "@/lib/url-params";
import { ACTIVE_WINDOWS, DELIVERY_CHANNELS, LANGUAGES } from "@/types/api";

const ALL = "all";
const LANGUAGE_LABELS: Record<string, string> = { uz: "O'zbekcha", ru: "Русский", en: "English" };

/**
 * Dashboard filtrlari — hammasi manzilda (`from`, `to`, `lang`, `channel`,
 * `active`): havola ulashilsa aynan shu kesim ochiladi, `RememberParams`
 * esa qaytib kelganda tiklaydi.
 */
export function DashboardFilters({ value, today }: { value: DashboardParams; today: string }) {
  const { t } = useTranslation();
  const [, setParams] = useSearchParams();
  const preset = matchPreset(value.from, value.to, today);

  const set = (changes: Record<string, string | number | undefined>) =>
    setParams(prev => withParams(prev, changes));

  const pickPreset = (next: DashboardPreset | "custom") => {
    if (next === "custom") return;
    const range = presetRange(next, today);
    // 30 kun — standart: manzilda yozilmaydi.
    set(next === "30d" ? { from: undefined, to: undefined } : range);
  };

  const dirty = preset !== "30d" || !!value.language || !!value.channel || value.activeDays !== 7;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Segmented<DashboardPreset | "custom">
        label={t("dashboard.filters.period")}
        value={preset ?? "custom"}
        onChange={pickPreset}
        options={DASHBOARD_PRESETS.map(item => ({
          value: item,
          label: t(`dashboard.presets.${item}`),
        }))}
      />

      <label className="bg-card flex h-10 items-center gap-1.5 rounded-2xl border px-3 text-sm shadow-xs">
        <CalendarRange className="text-muted-foreground size-4" aria-hidden />
        <input
          type="date"
          aria-label={t("dashboard.filters.from")}
          value={value.from}
          max={value.to}
          onChange={event => event.target.value && set({ from: event.target.value, to: value.to })}
          className="bg-transparent tabular-nums outline-none"
        />
        <span className="text-muted-foreground">—</span>
        <input
          type="date"
          aria-label={t("dashboard.filters.to")}
          value={value.to}
          min={value.from}
          max={today}
          onChange={event =>
            event.target.value && set({ from: value.from, to: event.target.value })
          }
          className="bg-transparent tabular-nums outline-none"
        />
      </label>

      <Select
        value={value.language ?? ALL}
        onValueChange={next => set({ lang: next === ALL ? undefined : next })}
      >
        <SelectTrigger
          aria-label={t("dashboard.filters.language")}
          className="h-10 w-40 rounded-2xl"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t("dashboard.filters.allLanguages")}</SelectItem>
          {LANGUAGES.map(lang => (
            <SelectItem key={lang} value={lang}>
              {LANGUAGE_LABELS[lang]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={value.channel ?? ALL}
        onValueChange={next => set({ channel: next === ALL ? undefined : next })}
      >
        <SelectTrigger
          aria-label={t("dashboard.filters.channel")}
          className="h-10 w-40 rounded-2xl"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t("dashboard.filters.allChannels")}</SelectItem>
          {DELIVERY_CHANNELS.map(channel => (
            <SelectItem key={channel} value={channel}>
              {t(`deliveries.channels.${channel}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Segmented<string>
        label={t("dashboard.filters.activeWindow")}
        value={String(value.activeDays)}
        onChange={next => set({ active: next === "7" ? undefined : next })}
        options={ACTIVE_WINDOWS.map(days => ({
          value: String(days),
          label: t("dashboard.filters.activeDays", { count: days }),
        }))}
      />

      {dirty ? (
        <button
          type="button"
          onClick={() =>
            set({
              from: undefined,
              to: undefined,
              lang: undefined,
              channel: undefined,
              active: undefined,
            })
          }
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-1 text-xs"
        >
          <RotateCcw className="size-3.5" aria-hidden />
          {t("dashboard.filters.reset")}
        </button>
      ) : null}
    </div>
  );
}
